// src/pages/transactions/TransactionsPage.js

import React, { useMemo, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import GenericListPage from '../GenericListPage';
import { format } from 'date-fns';
import { AuthContext } from '../../contexts/AuthContext'; // Import AuthContext
import useApi from '../../hooks/useApi'; // <-- import API hook

// --- small CSV helpers (keeps implementation local and simple) ---
function escapeCsv(val) {
  if (val == null) return '';
  return String(val).replace(/"/g, '""');
}
function jsonToCsv(items = [], cols = []) {
  const header = cols.map(c => `"${escapeCsv(c.header || c.label || c.key)}"`).join(',');
  const rows = items.map(item =>
    cols.map(col => {
      let v = '';
      if (typeof col.transform === 'function') v = col.transform(item);
      else v = col.key ? (col.key.split('.').reduce((o,k)=> (o ? o[k] : ''), item)) : '';
      return `"${escapeCsv(v ?? '')}"`;
    }).join(',')
  ).join('\r\n');
  return `${header}\r\n${rows}`;
}
function downloadCsvString(csvString, filename = 'report.csv') {
  const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    URL.revokeObjectURL(url);
    a.remove();
  }, 500);
}

const TransactionsPage = () => {
  const navigate = useNavigate();
  const { settings } = useContext(AuthContext); // Get settings from context
  const api = useApi(); // api instance

  const transactionColumns = useMemo(() => {
    const currencySymbol = settings?.currencySymbol || '$'; // Use dynamic symbol with a fallback

    return [
      {
        header: 'Transaction ID',
        key: '_id',
        render: (transaction) => (
          <span className="font-mono text-xs text-gray-500" title={transaction._id}>
            {transaction._id.substring(0, 8)}...
          </span>
        ),
      },
      {
        header: 'Customer',
        key: 'customerName',
        render: (transaction) => transaction.customer?.name || transaction.customerName || 'Walk-in',
      },
      {
        header: 'Total',
        key: 'totalAmount',
        render: (transaction) => (
          <span className="font-semibold text-gray-900">
            {currencySymbol}{Number(transaction.totalAmount || 0).toFixed(2)}
          </span>
        ),
      },
      {
        header: 'Status',
        key: 'status',
        render: (transaction) => {
          const status = transaction.status || 'Completed';
          const statusColor =
            status === 'Completed' ? 'bg-green-100 text-green-800' :
            status === 'Pending' ? 'bg-yellow-100 text-yellow-800' :
            status === 'Refunded' ? 'bg-gray-100 text-gray-800' :
            'bg-red-100 text-red-800';
          return (
            <span className={`px-2.5 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full capitalize ${statusColor}`}>
              {status}
            </span>
          );
        },
      },
      {
        header: 'Payment',
        key: 'paymentMethod',
        render: (transaction) => (
          <span className="px-2.5 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
            {transaction.paymentMethod || 'N/A'}
          </span>
        )
      },
      {
        header: 'Date',
        key: 'transactionDate',
        render: (transaction) => format(new Date(transaction.transactionDate), 'MMM d, yyyy'),
      },
    ];
  }, [settings]); // Re-render columns when settings change

  const transactionFilterConfig = useMemo(() => [
    {
      name: 'customerId',
      type: 'select',
      label: 'Customer',
      endpoint: 'customers',
      keyField: '_id',
      valueField: 'name',
    },
    {
      name: 'startDate',
      type: 'date',
      label: 'From Date',
    },
    {
      name: 'endDate',
      type: 'date',
      label: 'To Date',
    },
    {
      name: 'status',
      type: 'select',
      label: 'Status',
      options: [
        { value: 'Completed', label: 'Completed' },
        { value: 'Pending', label: 'Pending' },
        { value: 'Failed', label: 'Failed' },
        { value: 'Refunded', label: 'Refunded' },
      ]
    },
    {
      name: 'paymentMethod',
      type: 'select',
      label: 'Payment Method',
      options: [
        { value: 'Cash', label: 'Cash' },
        { value: 'Card', label: 'Card' },
        { value: 'UPI', label: 'UPI' },
      ]
    }
  ], []);

  const handleRowClick = (transaction) => {
    if (transaction._id) {
      navigate(`/transactions/${transaction._id}`);
    } else {
      console.error('Transaction ID not available for details.');
    }
  };

  // --- Export handler: will call existing API and send param to bypass pagination ---
  const handleDownloadCsv = async (currentFilters = {}) => {
    try {
      // Build query params: include filters/search and a param that tells backend to return all results.
      // Backend expected param name: noPagination=true.
      const queryParams = { ...currentFilters, noPagination: true, export: 'csv' };

      // Call existing endpoint using same api hook interface as GenericListPage
      const resp = await api.get('transactions', { queryParams });

      // Support both { data: [...] } and direct array responses
      const rows = Array.isArray(resp) ? resp : (resp?.data ?? []);

      if (!rows || rows.length === 0) {
        alert('No data found for the selected filters.');
        return;
      }

      // Prepare columns for CSV (adjust keys/labels if your schema differs)
      const cols = [
        { header: 'TransactionID', key: '_id' },
        { header: 'Customer', key: 'customer.name' },
        { header: 'Date', key: 'transactionDate', transform: (r) => r.transactionDate ? new Date(r.transactionDate).toISOString() : '' },
        { header: 'Total', key: 'totalAmount' },
        { header: 'Status', key: 'status' },
        { header: 'PaymentMethod', key: 'paymentMethod' },
      ];

      // For transform support inside jsonToCsv above: convert transform to be used below
      const normalizedCols = cols.map(c => ({
        header: c.header,
        key: c.key,
        transform: c.transform,
      }));

      // Build CSV rows honoring transform when present
      const csvString = (() => {
        const header = normalizedCols.map(c => `"${escapeCsv(c.header)}"`).join(',');
        const body = rows.map(row => normalizedCols.map(col => {
          let v = '';
          if (typeof col.transform === 'function') v = col.transform(row);
          else if (col.key) v = col.key.split('.').reduce((o,k)=> (o ? o[k] : ''), row);
          return `"${escapeCsv(v ?? '')}"`;
        }).join(',')).join('\r\n');
        return `${header}\r\n${body}`;
      })();

      const filename = `transactions-${new Date().toISOString().slice(0,10)}.csv`;
      downloadCsvString(csvString, filename);
    } catch (err) {
      console.error('CSV export failed', err);
      alert('Unable to export CSV. Check console for details.');
    }
  };

  return (
    <GenericListPage
      title="Transactions"
      permission="manage_transactions"
      entityEndpoint="transactions"
      columns={transactionColumns}
      onRowClick={handleRowClick}
      FormComponent={null} // No edit/delete actions on this page
      filterConfig={transactionFilterConfig}
      onDownloadCsv={handleDownloadCsv} // <-- provide export handler to GenericListPage
    />
  );
};

export default TransactionsPage;
